'use client';

import Link from 'next/link';
import { useId, useRef, useState } from 'react';

import ChevronIcon from '@/fo/icons/base/icons/chevron16.svg';
import DiamondIcon from '@/fo/icons/base/icons/diamond.svg';
import GiftCardIcon from '@/fo/icons/base/icons/giftcard.svg';
import { UiModPortalBase } from '@/fo/ui/mod/portal/base';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

import { styles } from './UiAppMenuBase.styles';

export type MenuItem = {
  label: string;
  href: string;
  className?: string;
  children?: MenuItem[];
};

export interface UiAppMenuBaseProps {
  className?: string;
  items: MenuItem[];
  additionalItems?: MenuItem[];
  style?: NativeElement['style'];
  onShow?: () => void;
  onHide?: () => void;
}

export const STOCK_CLASS_NAME = 'menu-item-stock';
export const GIFT_CARD_CLASS_NAME = 'menu-item-gift-card';

export function UiAppMenuBase({
  className,
  items = [],
  additionalItems = [],
  onShow,
  onHide,
  ...props
}: UiAppMenuBaseProps) {
  const refTimer = useRef(0);

  const [activeItem, setActiveItem] = useState<string[]>([]);

  const slots = styles();

  const changeActiveItem = (state: string[]) => {
    clearTimeout(refTimer.current);
    refTimer.current = window.setTimeout(() => {
      setActiveItem(state);
    }, 100);
  };

  const hideMenu = () => {
    changeActiveItem([]);
    onHide?.();
  };

  return (
    <div
      className={slots.base({ className })}
      data-nosnippet
      onMouseEnter={onShow}
      onMouseLeave={hideMenu}
      {...props}
    >
      <div className={slots.wrapper()}>
        {additionalItems && additionalItems?.length > 0 && (
          <div className={slots.group()}>
            <Items
              isGroup
              items={additionalItems}
              activeItem={activeItem}
              setActiveItem={setActiveItem}
            />
          </div>
        )}
        <Items
          items={items}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
        />
      </div>
      <div id="UiAppMenuBase-children-0"></div>
    </div>
  );
}

interface ItemsProps {
  classNameItem?: string;
  items?: MenuItem[];
  level?: number;
  isGroup?: boolean;
  activeItem: string[];
  setActiveItem: (key: string[]) => void;
  startIndex?: number;
}

const Items = ({
  classNameItem,
  items = [],
  level = 0,
  isGroup,
  activeItem,
  setActiveItem
}: ItemsProps) => {
  const slots = styles();
  const id = useId();

  const onActiveKeyChange = (key: string) => {
    const nextActiveKey = [...(activeItem || []).slice(0, level), key];
    setActiveItem(nextActiveKey);
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      {items.map((item, index) => {
        const menuNode = (
          <>
            <div className={slots.itemLabel()}>
              {item.className && (
                <div className={slots.itemCircleWrapper()}>
                  {item.className === GIFT_CARD_CLASS_NAME && <GiftCardIcon />}
                  {item.className === STOCK_CLASS_NAME && <DiamondIcon />}
                  {item.className !== STOCK_CLASS_NAME && (
                    <div
                      className={slots.itemCircle({
                        className: item.className
                      })}
                    />
                  )}
                </div>
              )}
              <span>{item.label}</span>
            </div>
            {item.children && <ChevronIcon />}
          </>
        );

        const itemKey = `${id}-${item.label}-${item.href}-${index}`;

        const childrenNode = activeItem[level] === itemKey &&
          item.children &&
          item.children.length > 0 && (
            <div className={slots.wrapper()}>
              <Items
                level={level + 1}
                activeItem={activeItem}
                setActiveItem={setActiveItem}
                items={item.children}
              ></Items>
            </div>
          );

        return (
          <Link
            key={itemKey}
            href={item.href}
            className={slots.item({
              link: true,
              className: classNameItem,
              itemActive: activeItem[level] === itemKey,
              itemGroup: isGroup
            })}
            onMouseEnter={() => onActiveKeyChange(itemKey)}
            onTouchEnd={(e) => {
              e.stopPropagation();
              if (item.children && item.children?.length > 0) {
                e.preventDefault();
                onActiveKeyChange(itemKey);
              }
            }}
          >
            {menuNode}
            {childrenNode && (
              <UiModPortalBase
                getContainer={() =>
                  document.getElementById(`UiAppMenuBase-children-${level}`)
                }
              >
                <div className={slots.children()}>
                  {childrenNode}
                  <div id={`UiAppMenuBase-children-${level + 1}`}></div>
                </div>
              </UiModPortalBase>
            )}
          </Link>
        );
      })}
    </>
  );
};
